<template>
    <section class="x-posts-section">
        <div class="container">
            <div class="section-header">
                <h2 class="section-title">{{ title }}</h2>
            </div>
            
            <div class="waterfall-flow">
                <div 
                    v-for="(tweet, index) in tweets" 
                    :key="index" 
                    :data-tweet-id="getTweetId(tweet)" 
                    v-html="tweet"
                    class="twitter-embed-container"
                ></div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import { onMounted, nextTick } from 'vue'

// Props
interface Props {
    title: string
    tweetUrls: string[]
}

const props = defineProps<Props>()

// Generate Twitter embed HTML from URLs
const tweets = props.tweetUrls.map(url => {
    const match = url.match(/status\/(\d+)/)
    const statusId = match ? match[1] : ''
    const userMatch = url.match(/(?:twitter\.com|x\.com)\/([^\/]+)/)
    const username = userMatch ? userMatch[1] : ''
    
    // Convert x.com to twitter.com for better compatibility
    const twitterUrl = url.replace('x.com', 'twitter.com')
    
    return `<blockquote class="twitter-tweet"><p dir="ltr" lang="en">Loading tweet... <a href="https://t.co/placeholder" rel="nofollow noopener" target="_blank">pic.twitter.com/placeholder</a></p>— ${username} (@${username}) <a href="${twitterUrl}?ref_src=twsrc%5Etfw" rel="nofollow noopener" target="_blank">December 20, 2024</a></blockquote>`
})

// Extract tweet ID from HTML
const getTweetId = (tweetHtml: string) => {
    const match = tweetHtml.match(/status\/(\d+)/)
    return match ? match[1] : ''
}

// Global error handler for Twitter embed errors
const setupErrorHandler = () => {
    // Capture unhandled promise rejections from Twitter embed
    window.addEventListener('unhandledrejection', (event) => {
        if (event.reason && typeof event.reason === 'object') {
            const errorMessage = event.reason.message || String(event.reason)
            if (errorMessage.includes('appendChild') || errorMessage.includes('embed')) {
                // Suppress Twitter embed errors - they don't affect functionality
                event.preventDefault()
                return false
            }
        }
    })

    // Capture general errors from Twitter embed
    const originalError = window.onerror
    window.onerror = (message, source, lineno, colno, error) => {
        if (typeof message === 'string' && 
            (message.includes('appendChild') || message.includes('embed.') || message.includes('twitter'))) {
            // Suppress Twitter embed errors
            return true
        }
        // Call original error handler if it exists
        if (originalError) {
            return originalError(message, source, lineno, colno, error)
        }
        return false
    }
}

// Twitter Widget Loading
onMounted(() => {
    // Setup error handler first
    setupErrorHandler()

    // Wait for DOM to be fully ready
    nextTick(() => {
        // Delayed loading of Twitter script to avoid conflicts with other scripts
        setTimeout(() => {
            try {
                // Check if Twitter script is already loaded
                if (document.querySelector('script[src*="platform.twitter.com/widgets.js"]')) {
                    // If script exists, just reload widgets after ensuring DOM is ready
                    nextTick(() => {
                        setTimeout(() => {
                            const twttr = (window as { twttr?: { widgets?: { load: () => void } } }).twttr
                            if (twttr && twttr.widgets && typeof twttr.widgets.load === 'function') {
                                try {
                                    twttr.widgets.load()
                                } catch (error) {
                                    // Silently handle Twitter widget errors
                                }
                            }
                        }, 200)
                    })
                    return
                }

                const script = document.createElement('script')
                script.src = 'https://platform.twitter.com/widgets.js'
                script.async = true
                script.charset = 'utf-8'
                script.crossOrigin = 'anonymous'
                
                // Add error handling for script loading
                script.onerror = () => {
                    console.warn('Failed to load Twitter widgets script')
                }
                
                // Safely add to head
                if (document.head && document.head instanceof Node) {
                    document.head.appendChild(script)
                }
                
                // Initialize Twitter widgets after script loads with error handling
                script.onload = () => {
                    try {
                        // Wait for Twitter script to fully load and DOM to be ready
                        nextTick(() => {
                            setTimeout(() => {
                                const twttr = (window as { twttr?: { widgets?: { load: () => void } } }).twttr
                                if (twttr && twttr.widgets && typeof twttr.widgets.load === 'function') {
                                    try {
                                        twttr.widgets.load()
                                    } catch (error) {
                                        // Silently handle Twitter widget errors
                                    }
                                }
                            }, 300)
                        })
                    } catch (error) {
                        // Silently handle initialization errors
                    }
                }
            } catch (error) {
                console.warn('Error loading Twitter widgets:', error)
            }
        }, 1500) // Increased delay to ensure DOM is ready
    })
})
</script>

<style lang="less" scoped>
.container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 1rem;
}

/* X Posts Section */
.x-posts-section {
    padding: 6rem 0;
    background-color: #22242d;
}

.section-header {
    text-align: center;
    margin-bottom: 3rem;
}

.section-title {
    font-size: 3rem;
    font-weight: 700;
    margin-bottom: 1rem;
    color: white;
    line-height: 1.2;
}

.waterfall-flow {
    columns: 4;
    max-width: 1400px;
    margin: 0 auto;
    gap: 20px 20px;
    
    @media (max-width: 1200px) {
        columns: 3;
    }
    
    @media (max-width: 768px) {
        columns: 2;
        column-gap: 0.75rem;
    }
    
    @media (max-width: 480px) {
        columns: 1;
    }
    
    > div {
        break-inside: avoid;
        display: inline-block;
        width: 100%;
    }
    
    :deep(.twitter-tweet) {
        margin: 0 !important;
        max-width: 100% !important;
    }
    
    .twitter-embed-container {
        position: relative;
        overflow: hidden;
        
        /* 确保容器内的内容不会导致布局问题 */
        :deep(*) {
            max-width: 100% !important;
            box-sizing: border-box;
            border-radius: 20px;
        }
    }
}

/* Responsive Design */
@media (max-width: 768px) {
    .section-title {
        font-size: 2.5rem;
    }
}

@media (max-width: 480px) {
    .section-title {
        font-size: 2rem;
    }
}
</style>

